import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { last } from 'lodash';
import moment from 'moment';

import { browserLog } from '~/utils/helper';

export type PDFDocumentDetails = {
  title: string;
  subtitle?: string;
};

export type PDFConfig = {
  doc: jsPDF;
  margin: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  pdfFileName: string;
};

type GeneratePDFStyleOverride = {
  selectors: string[];
  styles: {
    [p: string]: string;
  };
};

type GeneratePDFProps = {
  fileName?: string;
  documentDetails: PDFDocumentDetails;
  contentSize?: {
    width?: number;
    height?: number;
  };
  addContent?: (config: PDFConfig) => jsPDF;
  config?: {
    includeMarginsToPageSize?: boolean;
    backgroundSelectors?: string[];
    backgroundColor?: string;
    styleOverrideRules?: GeneratePDFStyleOverride[];
  };
};

export const generateSolaforcePDF = (props: GeneratePDFProps): PDFConfig => {
  const {
    fileName,
    documentDetails,
    contentSize: definedContentSize,
    addContent,
    config,
  } = props;

  const contentSize = {
    width: definedContentSize?.width ?? 800,
    height: definedContentSize?.height ?? 600,
  };

  const pdfFileName = `${fileName ?? 'unknown_file'}.pdf`;

  const pageMargins = {
    vertical: 50,
    horizontal: 50,
  };
  const timestamp = {
    fontSize: 10,
  };

  // Solaforce Logo
  const img = new Image();
  img.src = '/v2/img/logoBlueFooter.png';

  const imgSize = {
    width: 116,
    height: 24,
  };

  const resolveLogoPosition = (
    pageWidth: number,
    width: number,
    height: number,
  ) => {
    return {
      x: pageWidth - width - pageMargins.horizontal,
      y: pageMargins.vertical - height,
      w: width,
      h: height,
    };
  };

  // Document Details
  const resolveDocumentDetails = () => {
    const { title, subtitle } = documentDetails;

    const documentDetailRows = [
      {
        index: 1,
        text: title,
        fontSize: 20,
      },
      {
        index: 2,
        text: subtitle ?? '',
        fontSize: 15,
        color: [128, 128, 128],
      },
    ]
      .filter(({ text }) => {
        return !!text && text.length > 0;
      })
      .map((row, index, array) => {
        const previousRowHeights = array.slice(0, index);

        return {
          ...row,
          rowOffset: previousRowHeights.reduce(
            (total, { fontSize }) => total + fontSize * 1.3,
            0,
          ),
        };
      });

    const documentHeadBuffer = 15;
    const lastOffset = last(documentDetailRows)?.rowOffset;
    const detailsHeight = !!lastOffset ? lastOffset + documentHeadBuffer : 0;

    const logoStampHeight =
      imgSize.height + timestamp.fontSize + documentHeadBuffer;

    return {
      rows: documentDetailRows,
      height: detailsHeight > logoStampHeight ? detailsHeight : logoStampHeight,
    };
  };

  const documentDetailsSection = resolveDocumentDetails();

  const includeMargins = (size: number, margin: number) => {
    return !!config?.includeMarginsToPageSize ? size + margin * 2 : size;
  };

  const pageSize = {
    width: includeMargins(contentSize.width, pageMargins.horizontal),
    height: includeMargins(
      contentSize.height + documentDetailsSection.height,
      pageMargins.vertical,
    ),
  };

  // Construct PDF
  const doc = new jsPDF({
    orientation: pageSize.height < pageSize.width ? 'l' : 'p',
    unit: 'px',
    format: [pageSize.height, pageSize.width],
    hotfixes: ['px_scaling'],
  });

  // Include Document details
  doc.setTextColor(0, 0, 0);
  documentDetailsSection.rows.forEach((row) => {
    const { text, fontSize, rowOffset, color } = row;

    doc.setFontSize(fontSize);
    if (!!color) {
      const [ch1, ch2, ch3] = color;
      doc.setTextColor(ch1, ch2, ch3);
    }
    doc.text(text, pageMargins.horizontal, pageMargins.vertical + rowOffset);
  });

  // Include Solaforce Logo
  const logo = resolveLogoPosition(
    pageSize.width,
    imgSize.width,
    imgSize.height,
  );
  doc.addImage(img, 'PNG', logo.x, logo.y, logo.w, logo.h);

  // Include Timestamp
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(timestamp.fontSize);
  doc.text(
    moment().format('LLLL'),
    pageSize.width - pageMargins.horizontal,
    pageMargins.vertical + logo.h,
    {
      align: 'right',
    },
  );
  const margin = {
    top: pageMargins.vertical + documentDetailsSection.height,
    left: pageMargins.horizontal,
    right: pageMargins.horizontal,
    bottom: pageMargins.vertical,
  };

  const pdfConfig: PDFConfig = {
    doc,
    margin,
    pdfFileName,
  };

  if (!!addContent) {
    addContent(pdfConfig).save(pdfFileName);
  }

  return pdfConfig;
};

export const generateSolaforcePDFFromReact = (
  props: Omit<GeneratePDFProps, 'addContent'> & {
    content: HTMLElement;
  },
) => {
  const { content, contentSize: overrideContentSize, ...generateProps } = props;

  const contentSize = {
    width:
      overrideContentSize?.width ??
      Math.max(content.scrollWidth, content.offsetWidth),
    height:
      overrideContentSize?.height ??
      Math.max(content.scrollHeight, content.offsetHeight),
  };

  const { doc, margin, pdfFileName } = generateSolaforcePDF({
    ...generateProps,
    contentSize,
    config: {
      includeMarginsToPageSize: true,
    },
  });

  const {
    content: preparedContent,
    cleanup,
    waitForImages,
  } = prepareContentForPdf(content, contentSize, {
    backgroundSelectors: generateProps.config?.backgroundSelectors,
    backgroundColor: generateProps.config?.backgroundColor,
    styleOverrideRules: generateProps.config?.styleOverrideRules,
  });

  waitForImages()
    .then(() =>
      html2canvas(preparedContent, {
        scale: 1,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: contentSize.width,
        height: contentSize.height,
        windowWidth: contentSize.width,
        windowHeight: contentSize.height,
      }),
    )
    .then((canvas) => {
      const imageData = canvas.toDataURL('image/png');
      doc.addImage(
        imageData,
        'PNG',
        margin.left,
        margin.top,
        contentSize.width,
        contentSize.height,
      );
      doc.save(pdfFileName);
    })
    .finally(() => {
      cleanup();
    });
};

// Helper to resolve SVG size
const resolveSvgSize = (svg: SVGSVGElement) => {
  const attrWidth = parseFloat(svg.getAttribute('width') ?? '');
  const attrHeight = parseFloat(svg.getAttribute('height') ?? '');
  if (Number.isFinite(attrWidth) && Number.isFinite(attrHeight)) {
    if (attrWidth > 0 && attrHeight > 0) {
      return { width: Math.ceil(attrWidth), height: Math.ceil(attrHeight) };
    }
  }

  const rect = svg.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    return { width: Math.ceil(rect.width), height: Math.ceil(rect.height) };
  }

  try {
    const bBox = svg.getBBox();
    if (bBox.width > 0 && bBox.height > 0) {
      return { width: Math.ceil(bBox.width), height: Math.ceil(bBox.height) };
    }
  } catch (error) {
    return { width: 0, height: 0 };
  }

  return { width: 0, height: 0 };
};

// Helper to convert SVG to Image for better html2canvas compatibility
const svgToDataUrl = (svg: SVGSVGElement) => {
  const cloned = svg.cloneNode(true) as SVGSVGElement;
  if (!cloned.getAttribute('xmlns')) {
    cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  const { width, height } = resolveSvgSize(svg);

  if (!cloned.getAttribute('width') && width > 0) {
    cloned.setAttribute('width', width.toString());
  }
  if (!cloned.getAttribute('height') && height > 0) {
    cloned.setAttribute('height', height.toString());
  }

  const serialized = new XMLSerializer().serializeToString(cloned);
  const encoded = encodeURIComponent(serialized)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');

  return `data:image/svg+xml;charset=utf-8,${encoded}`;
};

// Converts an SVG element to an Image element by creating a data URL from the SVG content.
const svgToImg = (svg: SVGSVGElement) => {
  const img = new Image();
  img.src = svgToDataUrl(svg);

  const { width, height } = resolveSvgSize(svg);
  if (width > 0) {
    img.width = width;
  }
  if (height > 0) {
    img.height = height;
  }

  return img;
};

// Converts a Canvas element to an Image element by creating a data URL from the canvas content.
const canvasToImg = (canvas: HTMLCanvasElement) => {
  try {
    const img = new Image();
    img.src = canvas.toDataURL('image/png');
    img.width = canvas.width;
    img.height = canvas.height;
    return img;
  } catch (error) {
    return null;
  }
};

// Prepares the content for PDF generation by cloning it, replacing SVGs and Canvases with Images.
const prepareContentForPdf = (
  content: HTMLElement,
  contentSize: { width: number; height: number },
  options?: {
    backgroundSelectors?: string[];
    backgroundColor?: string;
    styleOverrideRules?: GeneratePDFStyleOverride[];
  },
) => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = `${contentSize.width}px`;
  container.style.height = `${contentSize.height}px`;
  container.style.background = 'white';
  container.style.overflow = 'visible';

  const clonedContent = content.cloneNode(true) as HTMLElement;
  clonedContent.style.overflow = 'visible';
  clonedContent.style.height = 'auto';
  clonedContent.style.maxHeight = 'none';
  container.appendChild(clonedContent);
  document.body.appendChild(container);

  if (options?.backgroundSelectors?.length) {
    const bgColor = options?.backgroundColor ?? '#ffffff';

    overrideElementStyles(clonedContent, [
      {
        selectors: options.backgroundSelectors,
        styles: {
          background: bgColor,
          'background-color': bgColor,
          'background-image': 'none',
        },
      },
    ]);
  }

  if (options?.styleOverrideRules?.length) {
    overrideElementStyles(clonedContent, options.styleOverrideRules);
  }

  const images: HTMLImageElement[] = [];
  const svgElements = Array.from(clonedContent.querySelectorAll('svg'));
  svgElements.forEach((svgElement) => {
    const { width, height } = resolveSvgSize(svgElement);
    if (width > 0 && height > 0) {
      const image = svgToImg(svgElement);
      images.push(image);
      if (svgElement.parentNode) {
        svgElement.parentNode.replaceChild(image, svgElement);
      }
    }
  });

  const canvasElements = Array.from(clonedContent.querySelectorAll('canvas'));
  canvasElements.forEach((canvasElement) => {
    const image = canvasToImg(canvasElement);
    if (image && canvasElement.parentNode) {
      images.push(image);
      canvasElement.parentNode.replaceChild(image, canvasElement);
    }
  });

  const waitForImages = () => {
    const waits = images.map((image) => {
      if (image.complete) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });
    });

    return Promise.all(waits).then(() => undefined);
  };

  const cleanup = () => {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  return { container, content: clonedContent, cleanup, waitForImages };
};

const overrideElementStyles = (
  root: HTMLElement,
  rules: GeneratePDFStyleOverride[],
) => {
  rules.forEach(({ selectors, styles }) => {
    selectors.forEach((selector) => {
      root.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        browserLog(`Generate PDF - override styles on element: ${selector}`);
        Object.entries(styles).forEach(([property, value]) => {
          element.style.setProperty(property, value, 'important');
        });
      });
    });
  });
};

export const commonPDFStyleOverrides = {
  muiPaper: {
    selectors: ['.MuiPaper-root'],
    styles: {
      'background-color': '#ffffff',
      'box-shadow': 'none',
      border: 'solid 1px #ccc',
    },
  },
};
